import { createContext, useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loginUser, logoutUser, checkCreator } from '../services/api';
import { Loader2 } from 'lucide-react';
import logo_long from '../assets/logo_long.png';
import { identifyUser } from '../utils/gtmAnalytics';

export const AuthContext = createContext();

// Helper function to remove league_details entries based on scoring condition
const cleanLeagueDetails = (response) => {
  if (response.league_details && Array.isArray(response.league_details)) {
    response.league_details = response.league_details.filter(
      (entry) =>
        !(
          entry &&
          entry.matchkeys &&
          entry.matchkeys.game === 'OFPL Draft' &&
          entry.data &&
          entry.data.league &&
          entry.data.league.scoring === 'm'
        )
    );
  }
  return response;
};

// Helper function to preserve all authentication data
const processAuthResponse = (response) => {
  const cleanedResponse = cleanLeagueDetails(response);

  return {
    ...cleanedResponse,
    has_active_subscription: response.has_active_subscription,
    subscriptions: response.subscriptions,
    new_subscriptions: response.new_subscriptions,
    user: response.user,
    token: response.token,
    gw_status: response.gw_status,
    season: response.season,
    token_valid_until: response.token_valid_until,
    // Migration flags (admin doesn't block, just informational)
    needs_password_migration: response.needs_password_migration || false,
    needs_subscription_migration: response.needs_subscription_migration || false,
    has_access: response.has_access !== undefined ? response.has_access : true,
    access_reason: response.access_reason || 'subscription',
    is_preseason: response.is_preseason || false,
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const hasAttemptedAuth = useRef(false);

  // On mount, check for token in localStorage
  useEffect(() => {
    if (hasAttemptedAuth.current) {
      return;
    }
    hasAttemptedAuth.current = true;

    const authenticateWithToken = async () => {
      const token = localStorage.getItem('token');

      if (!token) {
        if (location.pathname !== '/login') {
          navigate('/login', { replace: true });
        }
        setIsAuthLoading(false);
        return;
      }

      try {
        const response = await loginUser(null, null, token);

        if (response && response.token) {
          const processedResponse = processAuthResponse(response);

          // Check if user is a content creator
          const wpId = processedResponse.user?.wp_id;
          if (wpId) {
            try {
              const creatorCheck = await checkCreator(wpId);
              if (!creatorCheck.is_content_creator) {
                // User is not a creator, deny access
                localStorage.removeItem('token');
                localStorage.removeItem('activeLeague');
                if (location.pathname !== '/login') {
                  navigate('/login', { replace: true, state: { error: 'Creator access required' } });
                }
                setIsAuthLoading(false);
                return;
              }
            } catch (creatorError) {
              console.error('Failed to check creator status:', creatorError);
              // On error, deny access for security
              localStorage.removeItem('token');
              localStorage.removeItem('activeLeague');
              if (location.pathname !== '/login') {
                navigate('/login', { replace: true, state: { error: 'Failed to verify creator status' } });
              }
              setIsAuthLoading(false);
              return;
            }
          }

          // Set user data immediately - let LeagueContext handle league validation
          setUser(processedResponse);

          // Identify user in GTM (token-based returning user)
          identifyUser(processedResponse, 'token');

          if (response.token !== token) {
            localStorage.setItem('token', response.token);
          }

          // Handle navigation (creators without leagues still get the hub;
          // only the Lineups page needs an active league)
          if (location.pathname === '/login') {
            navigate('/');
          }
        } else {
          localStorage.removeItem('token');
          localStorage.removeItem('activeLeague');
          if (location.pathname !== '/login') {
            navigate('/login', { replace: true });
          }
        }
      } catch (error) {
        console.error('Token-based login failed:', error);

        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem('token');
          localStorage.removeItem('activeLeague');
          if (location.pathname !== '/login') {
            navigate('/login', { replace: true });
          }
        } else {
          console.error('Network or server error, keeping token for retry');
        }
      } finally {
        setIsAuthLoading(false);
      }
    };

    authenticateWithToken();
  }, [location.pathname, navigate]);

  const login = async (email, password) => {
    try {
      const response = await loginUser(email, password);
      if (!response) {
        return { success: false, message: 'Incorrect login details' };
      }

      const processedResponse = processAuthResponse(response);

      // Store the token BEFORE checkCreator — the endpoint requires an
      // authenticated session, and the axios interceptor reads localStorage.
      localStorage.setItem('token', processedResponse.token);

      // Check if user is a content creator before allowing access
      const wpId = processedResponse.user?.wp_id;
      if (wpId) {
        try {
          const creatorCheck = await checkCreator(wpId);
          if (!creatorCheck.is_content_creator) {
            // User is not a creator, deny access
            localStorage.removeItem('token');
            return { success: false, message: 'Creator access required. The creator hub is invitation-only — contact DraftFC to become a creator.' };
          }
        } catch (creatorError) {
          console.error('Failed to check creator status:', creatorError);
          localStorage.removeItem('token');
          return { success: false, message: 'Failed to verify creator status. Please try again.' };
        }
      } else {
        localStorage.removeItem('token');
        return { success: false, message: 'Unable to verify user identity' };
      }

      setUser(processedResponse);

      // Identify user in GTM (email/password login)
      identifyUser(processedResponse, 'email');

      navigate('/');
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      localStorage.removeItem('token');
      return { success: false, message: 'Login failed' };
    }
  };

  const logout = async () => {
    const token = localStorage.getItem('token');

    // Call API to delete session from database
    if (token) {
      try {
        await logoutUser(token);
      } catch (error) {
        console.error('Failed to delete session from database:', error);
        // Continue with local logout even if API call fails
      }
    }

    // Clear local storage
    localStorage.removeItem('token');
    localStorage.removeItem('activeLeague');

    // Clear state
    setUser(null);
    hasAttemptedAuth.current = false;

    // Navigate to login
    navigate('/login', { replace: true });
  };

  const refreshUserData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      return false;
    }

    try {
      const response = await loginUser(null, null, token);
      if (response && response.token) {
        const processedResponse = processAuthResponse(response);

        // Set user data immediately - let LeagueContext handle league validation
        setUser(processedResponse);

        if (response.token !== token) {
          localStorage.setItem('token', response.token);
        }

        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to refresh user data:', error);

      // If refresh fails due to expired token, redirect to login
      if (error.response?.status === 401 || error.response?.status === 403) {
        await logout();
      }

      return false;
    }
  };

  if (isAuthLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          flexDirection: 'column',
          gap: '30px',
        }}
      >
        <img
          src={logo_long}
          alt="Logo"
          style={{ maxWidth: '200px', width: '100%', height: 'auto' }}
        />
        <Loader2 className="h-10 w-10 animate-spin text-primary" aria-hidden="true" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, refreshUserData, isAuthLoading }}>
      {children}
    </AuthContext.Provider>
  );
};
